import React from "react";

// prefs.jsx — customer-facing preference state: theme mode, accent color,
// text size, high contrast, the aria-live announcer, and the optional
// device-local profile. Every preference persists in localStorage and is
// applied as classes/attributes on <html>, so the choice survives across
// every page without any account or backend.
//
// Keys:
//   cc-theme            — "light" | "dark" | "system"   (shared with theme.js;
//                           the live site's useTheme() reads the same key,
//                           so the workbench toggle and the site header stay
//                           in sync)
//   cc-accent-v1        — "gold" | "rose" | "emerald"
//   cc-textsize-v1      — "standard" | "large" | "xlarge"
//   cc-contrast-v1      — "on" | "off"
//   cc-profile-v1       — { name, email } | null  (optional, device-local)
//
// First-load behavior: if the customer has never chosen a theme, we write
// "dark" — the brand default (Bianca, 2026-10-04) — so every first visit
// opens on the black-and-gold canvas. A saved choice ("light" | "dark" |
// "system") is never overwritten; "system" remains selectable for anyone
// who prefers following their OS setting. This runs at module import
// time — before any React effect — so theme.js's useTheme() picks up the
// "dark" value on its first render. theme.js itself is untouched.

const THEME_KEY = "cc-theme";
const ACCENT_KEY = "cc-accent-v1";
const TEXTSIZE_KEY = "cc-textsize-v1";
const CONTRAST_KEY = "cc-contrast-v1";
const PROFILE_KEY = "cc-profile-v1";

function readStored(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

// ── First-load defaults (run once, at import) ─────────────────────────────
function applyFirstLoadDefaults() {
  if (typeof document === "undefined" || typeof localStorage === "undefined") return;
  try {
    if (localStorage.getItem(THEME_KEY) == null) {
      // Brand default on first load: dark (never overrides a saved choice).
      localStorage.setItem(THEME_KEY, "dark");
    }
    const accent = localStorage.getItem(ACCENT_KEY);
    document.documentElement.setAttribute(
      "data-accent",
      ACCENTS.includes(accent) ? accent : "gold"
    );
    const ts = localStorage.getItem(TEXTSIZE_KEY);
    const cls = TEXTSIZE_CLASS[ts] || TEXTSIZE_CLASS.standard;
    document.documentElement.classList.add(cls);
    if (localStorage.getItem(CONTRAST_KEY) === "on") {
      document.documentElement.classList.add("cc-high-contrast");
    }
  } catch {}
}

// ── Accent color ──────────────────────────────────────────────────────────
// Black base, always. Accent options are Gold (default), Rose, Emerald —
// never navy/blue per the brand rule.
export const ACCENTS = ["gold", "rose", "emerald"];
export const ACCENT_HEX = { gold: "#FFC107", rose: "#FB7185", emerald: "#34D399" };

export function useAccentColor() {
  const [accent, setAccentState] = React.useState(() => {
    const v = readStored(ACCENT_KEY);
    return ACCENTS.includes(v) ? v : "gold";
  });
  React.useEffect(() => {
    writeStored(ACCENT_KEY, accent);
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-accent", accent);
    }
  }, [accent]);
  const setAccent = React.useCallback((next) => {
    if (ACCENTS.includes(next)) setAccentState(next);
  }, []);
  return [accent, setAccent];
}

// ── Text size (3 sizes) ────────────────────────────────────────────────────
export const TEXT_SIZES = ["standard", "large", "xlarge"];
const TEXTSIZE_CLASS = {
  standard: "cc-textsize-standard",
  large: "cc-textsize-large",
  xlarge: "cc-textsize-xlarge",
};

export function useTextSize() {
  const [size, setSizeState] = React.useState(() => {
    const v = readStored(TEXTSIZE_KEY);
    return TEXT_SIZES.includes(v) ? v : "standard";
  });
  React.useEffect(() => {
    writeStored(TEXTSIZE_KEY, size);
    if (typeof document !== "undefined") {
      const el = document.documentElement;
      Object.values(TEXTSIZE_CLASS).forEach((c) => el.classList.remove(c));
      el.classList.add(TEXTSIZE_CLASS[size]);
    }
  }, [size]);
  const setSize = React.useCallback((next) => {
    if (TEXT_SIZES.includes(next)) setSizeState(next);
  }, []);
  return [size, setSize];
}

// ── High contrast ─────────────────────────────────────────────────────────
export function useHighContrast() {
  const [on, setOn] = React.useState(() => readStored(CONTRAST_KEY) === "on");
  React.useEffect(() => {
    writeStored(CONTRAST_KEY, on ? "on" : "off");
    if (typeof document !== "undefined") {
      document.documentElement.classList.toggle("cc-high-contrast", on);
    }
  }, [on]);
  return [on, setOn];
}

// ── Screen-reader announcer ───────────────────────────────────────────────
// <RouteAnnouncer/> renders the aria-live region once per page. announce()
// lets any component speak through it (form errors, step changes, etc.).
const announcerSubscribers = new Set();

export function announce(message) {
  announcerSubscribers.forEach((fn) => {
    try {
      fn(message);
    } catch {}
  });
}

function patchHistory() {
  if (typeof window === "undefined" || window.__ccHistoryPatched) return null;
  const origPush = window.history.pushState.bind(window.history);
  const origReplace = window.history.replaceState.bind(window.history);
  const fire = () => window.dispatchEvent(new Event("cc:routechange"));
  window.history.pushState = (...args) => {
    origPush(...args);
    fire();
  };
  window.history.replaceState = (...args) => {
    origReplace(...args);
    fire();
  };
  window.__ccHistoryPatched = true;
  return () => {
    window.history.pushState = origPush;
    window.history.replaceState = origReplace;
    window.__ccHistoryPatched = false;
  };
}

/** Polite live region that announces the page title on every route change.
 *  Mount once inside the global site chrome. */
export function RouteAnnouncer() {
  const [message, setMessage] = React.useState("");
  React.useEffect(() => {
    announcerSubscribers.add(setMessage);
    const sayTitle = () => {
      const title = typeof document !== "undefined" ? document.title : "";
      if (title) setMessage(title);
    };
    const onRoute = () => {
      // Let the new page set its title first.
      setTimeout(sayTitle, 60);
    };
    const restore = patchHistory();
    window.addEventListener("popstate", onRoute);
    window.addEventListener("hashchange", onRoute);
    window.addEventListener("cc:routechange", onRoute);
    sayTitle();
    return () => {
      announcerSubscribers.delete(setMessage);
      window.removeEventListener("popstate", onRoute);
      window.removeEventListener("hashchange", onRoute);
      window.removeEventListener("cc:routechange", onRoute);
      if (restore) restore();
    };
  }, []);
  return (
    <div className="cc-sr-only" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
}

// ── Optional device-local profile (no backend, no account) ────────────────
// Guest checkout works 100% without this. When the customer opts in, we
// remember their name + email on this device only, so the order form can
// prefill. Nothing is sent anywhere; there is deliberately no sign-in,
// no password, no sync.
export function loadProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && (p.name || p.email)) {
      return { name: String(p.name || ""), email: String(p.email || "") };
    }
  } catch {}
  return null;
}

export function saveProfile({ name, email }) {
  try {
    localStorage.setItem(
      PROFILE_KEY,
      JSON.stringify({ name: String(name || "").trim(), email: String(email || "").trim() })
    );
  } catch {}
}

export function clearProfile() {
  try {
    localStorage.removeItem(PROFILE_KEY);
  } catch {}
}

export function useOptionalProfile() {
  const [profile, setProfileState] = React.useState(loadProfile);
  const save = React.useCallback((fields) => {
    saveProfile(fields);
    setProfileState(loadProfile());
  }, []);
  const clear = React.useCallback(() => {
    clearProfile();
    setProfileState(null);
  }, []);
  return { profile, save, clear };
}

applyFirstLoadDefaults();
